/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useRef } from "react";
import { fetchCoordinatesFromGeoCode } from "../../services/map/addressToCoordinates";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import OrderDetailsDialog from "../../components/Orders/OrderDetailsDialog";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import "../map/map.css";

const MapOrder = ({ orderData, onFilterChange, isLoading }) => {
  const [markers, setMarkers] = useState([{ lat: 0, lng: 0 }]);
  const [selectedMapOption, setSelectedMapOption] = useState("afterFeb2025");
  const [filteredOrderData, setFilteredOrderData] = useState([]);
  const [editOrderId, setEditOrderId] = useState(null);
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [selectedDeliveryStatuses, setSelectedDeliveryStatuses] = useState([]);
  const toast = useRef(null);

  const deliveryStatusOptions = [
    { name: "Need to Deliver", code: "Need to Deliver" },
    { name: "Tiki Bar Rental", code: "Tiki Bar Rental" },
    { name: "Palm Tree Rental", code: "Palm Tree Rental" },
    { name: "Delivery Scheduled", code: "Delivery Scheduled" },
    { name: "Delivered", code: "Delivered" },
    { name: "Took Trees", code: "Took Trees" },
    { name: "Picking Up", code: "Picking Up" },
  ];

  const handleDeliveryStatusChange = (e) => {
    const newSelectedStatuses = e.value;
    setSelectedDeliveryStatuses(newSelectedStatuses);

    // Call the callback to notify parent component
    if (onFilterChange) {
      onFilterChange({
        // Pass the array directly instead of stringifying it
        order_delivery_status: newSelectedStatuses.map((status) => status.code),
      });
    }
  };

  const getPinColor = (orderDeliveryStatus, id) => {
    switch (orderDeliveryStatus) {
      case "Need to Deliver":
      case "Need to Deliver (Tropicals)":
        return "#E12958"; // Red
      case "Tiki Bar Rental":
        return "#FFC42A"; // Yellow
      case "Palm Tree Rental":
        return "#9747FF"; // Purple
      case "Delivery Schedule":
      case "Delivery Scheduled":
        return "#22C55E"; // Light green
      case "Delivered":
        return "#000000"; // Black
      case "Took Trees":
        return "#FF8C00"; // Orange (keeping original color for this status)
      case "Picking Up":
        return "#9AE0FF"; // Light blue (keeping original color for this status)
      default:
        return "#808080"; // Default gray color for unknown status
    }
  };

  const editOrderDialog = (status, id, lat, lng) => {
    setEditOrderId(id);
    setOrderDetailsVisible(true);

    // Keep the clicked marker at the front (as done in MapService)
    const selectedMarker = markers.find((marker) => marker.id === id);
    if (selectedMarker) {
      const index = markers.findIndex((marker) => marker.id === id);
      const newMarkersArray = [
        selectedMarker,
        ...markers.slice(0, index),
        ...markers.slice(index + 1),
      ];
      setMarkers(newMarkersArray);
    }
  };

  const currentDate = new Date();
  const todayFormattedDate = currentDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  // Following MapService pattern for data filtering and processing
  useEffect(() => {
    if (orderData?.data) {
      let newFilteredData = [];

      // Apply date filtering - similar to MapService approach
      switch (selectedMapOption) {
        case "today":
          newFilteredData = orderData.data.filter(
            (order) =>
              todayFormattedDate ===
              order.order_delivery?.estimated_delivery_date
          );
          break;
        case "thisWeek":
          const addOrdersForDate = (date) => {
            const formattedCurrentDate = date.toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            });

            orderData.data.forEach((order) => {
              if (
                formattedCurrentDate ===
                order.order_delivery?.estimated_delivery_date
              ) {
                newFilteredData.push(order);
              }
            });
          };

          for (let i = 0; i <= 6; i++) {
            const today = new Date();
            const currentDate = new Date(today);
            currentDate.setDate(currentDate.getDate() + i);
            addOrdersForDate(currentDate);
          }
          break;
        case "schedule":
          newFilteredData = orderData.data.filter(
            (order) => !order.order_delivery?.estimated_delivery_date
          );
          break;
        case "allOrders":
          newFilteredData = orderData?.allOrderData?.data || [];
          break;
        default:
          newFilteredData = orderData.data;
      }

      setFilteredOrderData(newFilteredData);

      // Using async/await pattern from MapService
      const fetchCoordinates = async () => {
        try {
          const coordinates = await fetchCoordinatesFromGeoCode(
            newFilteredData
          );
          if (coordinates.length !== 0) {
            setMarkers(coordinates);
          } else {
            setMarkers([{ lat: 0, lng: 0 }]);
          }
        } catch (error) {
          console.error("Error fetching coordinates:", error);
          setMarkers([{ lat: 0, lng: 0 }]);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load map coordinates",
            life: 3000,
          });
        }
      };

      fetchCoordinates();
    }
  }, [orderData, selectedMapOption, todayFormattedDate]);

  const handleSelection = (e) => {
    setSelectedMapOption(e.target.value);
    setFilteredOrderData([]);
  };

  const mapContainerStyle = {
    width: "75vw",
    height: "70vh",
  };

  const center = {
    lat: markers[0]?.lat || 0,
    lng: markers[0]?.lng || 0,
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyCRVh8i3NvcSTjn9eZ3avLuP4eapTrXpG4",
  });

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Toast ref={toast} />
      <div className="flex justify-between items-center mb-3">
        <select
          id="dateOptions"
          className="h-10 text-xl w-60 rounded-xl"
          onChange={handleSelection}
          value={selectedMapOption}
        >
          <option value="afterFeb2025">After Feb 2025</option>
          {/* <option value="allOrders">All Orders</option> */}
          <option value="today">Today</option>
          <option value="thisWeek">This Week</option>
          <option value="schedule">Need to Schedule</option>
        </select>

        <div className="w-[25vw] h-10">
          <MultiSelect
            value={selectedDeliveryStatuses}
            options={deliveryStatusOptions}
            onChange={handleDeliveryStatusChange}
            optionLabel="name"
            placeholder="Select Delivery Status"
            display="chip"
            className="w-full rounded-xl"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center w-[75vw] h-[70vh]">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={8}
          center={center}
          defaultOptions={{ markerOptions: { openInfoWindowByDefault: true } }}
        >
          {markers?.map((marker, index) => (
            <MarkerF
              key={index}
              position={{ lat: marker?.lat, lng: marker?.lng }}
              icon={{
                path: "M213.285,0h-0.608C139.114,0,79.268,59.826,79.268,133.361c0,48.202,21.952,111.817,65.246,189.081c32.098,57.281,64.646,101.152,64.972,101.588c0.906,1.217,2.334,1.934,3.847,1.934c0.043,0,0.087,0,0.13-0.002c1.561-0.043,3.002-0.842,3.868-2.143c0.321-0.486,32.637-49.287,64.517-108.976c43.03-80.563,64.848-141.624,64.848-181.482C346.693,59.825,286.846,0,213.285,0zM274.865,136.62c0,34.124-27.761,61.884-61.885,61.884c-34.123,0-61.884-27.761-61.884-61.884s27.761-61.884,61.884-61.884C247.104,74.736,274.865,102.497,274.865,136.62z",
                fillColor: getPinColor(marker.orderDeliveryStatus, marker.id),
                fillOpacity: 1,
                strokeWeight: 0,
                rotation: 0,
                scale: 0.08,
              }}
              label={{
                text: marker?.customerData?.full_name || "",
                className: "custom-label",
              }}
              onClick={() => {
                editOrderDialog(
                  marker.status,
                  marker.id,
                  marker.lat,
                  marker.lng
                );
              }}
            />
          ))}
        </GoogleMap>
      )}

      {editOrderId && (
        <OrderDetailsDialog
          visible={orderDetailsVisible}
          setVisible={setOrderDetailsVisible}
          setOrderId={setEditOrderId}
          id={editOrderId}
        />
      )}
    </div>
  );
};

export default MapOrder;
